import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';

import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { classNames } from 'primereact/utils';
import { MessageContext } from '../../context/MessageContext';

import ResourceAutoComplete
    from '../../component/resourceautocomplete/ResourceAutoComplete';

import ProductSidebar from './ProductSidebar';

import {
    componentService,
    productComponentsService,
    productInventoryService,
    type ProductComponentDto,
    type ProductInventoryItemDto,
} from '../../service/warehouse';

// @ts-ignore
import { ReactComponent as AddComponentsIcon } from '../../assets/images/add-circle-icon.svg';
// @ts-ignore
import { ReactComponent as TopEditWhiteIcon } from '../../assets/images/edit-white-icon.svg';
// @ts-ignore
import { ReactComponent as RemoveLineIcon } from '../../assets/images/stock-levels/button-remove-line-red-bg.svg';
// @ts-ignore
import { ReactComponent as EmptyComponentIcon } from '../../assets/images/stock-levels/products-components-containers-empty.svg';
// @ts-ignore
import { ReactComponent as EditIcon } from '../../assets/images/stock-levels/button-document-edit.svg';
// @ts-ignore
import {
    ReactComponent as WarningTriangleIcon,
} from '../../assets/images/warning-triangle-icon.svg';

import { Dropdown } from 'primereact/dropdown';

type ComponentRowType = 'existing' | 'draft' | 'edit';

type ProductOption = {
    label: string;
    value: string;
    systemId: string;
    externalId: string;
    ean: string;
    uom: string;
    status: string;
    raw: ProductInventoryItemDto;
};

type ComponentRow = {
    tempId: string;
    rowType: ComponentRowType;

    // Internal Warehouse component UUID used by
    // PUT /api/warehouse/product/{productId}/components.
    componentId: string;

    // External UUID of a catalog product referenced directly in the BOM.
    productTargetId: string;

    // External ID of the source product/component.
    externalId: string;

    // Displayed product/system ID.
    productId: string;

    ean: string;
    product: string;
    usage: number;
    uom: string;
    averageCost: string;
    status: string;
    originalStatus: string;
    vatCode: string;
};

type Props = {
    productId: string;
};

const safeString = (value: unknown): string =>
    value === null || value === undefined ? '' : String(value);

const firstNonEmpty = (...values: unknown[]): string => {
    for (const value of values) {
        const text = safeString(value).trim();
        if (text) return text;
    }

    return '';
};

const getProductSystemId = (product: any): string =>
    safeString(
        product.systemId ??
        product.system_id ??
        product.id ??
        product.productId ??
        product.product_id,
    );

const getProductExternalId = (product: any): string =>
    safeString(product.externalId ?? product.external_id ?? product.externalID);

const getProductSubmitId = (product: any): string =>
    getProductExternalId(product) || getProductSystemId(product);

const getProductName = (product: any): string =>
    firstNonEmpty(
        product.name,
        product.productName,
        product.product_name,
        product.displayName,
        product.display_name,
        getProductExternalId(product),
        getProductSystemId(product),
    );

const getProductEan = (product: any): string =>
    safeString(product.ean ?? product.sku ?? product.code);

const getProductUom = (product: any): string =>
    safeString(
        product.uom ??
        product.udm ??
        product.unit ??
        product.unitOfMeasurement ??
        product.unitOfMeasure ??
        product.unit_of_measure,
    );

const getProductVatCode = (product: any): string =>
    safeString(
        product.vatCode ??
        product.vat_code ??
        product.vatCodeValue ??
        product.vat_code_value ??
        product.vatRate ??
        product.vat_rate,
    );

const mapProductToOption = (
    product: ProductInventoryItemDto,
): ProductOption | null => {
    const value = getProductSubmitId(product);

    const useAsComponent =
        (product as any).useAsComponent ??
        (product as any).use_as_component;

    if (
        !value ||
        !(
            useAsComponent === true ||
            useAsComponent === 'true'
        )
    ) {
        return null;
    }

    return {
        label: getProductName(product),
        value,
        systemId: getProductSystemId(product),
        externalId: getProductExternalId(product),
        ean: getProductEan(product),
        uom: getProductUom(product),
        status: safeString(product.status || 'ACTIVE'),
        raw: product,
    };
};

const mapProductComponentToRow = (
    item: ProductComponentDto,
): ComponentRow => {
    const source = item as any;

    const componentId = safeString(
        source.componentId ??
        source.component_id,
    );

    const productTargetId = safeString(
        source.productId ??
        source.product_id,
    );

    const targetId = componentId || productTargetId;

    return {
        tempId:
            targetId ||
            `component-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        rowType: 'existing',
        componentId,
        productTargetId,
        productId: safeString(
            source.productSystemId ??
            source.product_system_id ??
            source.componentSystemId ??
            source.component_system_id ??
            targetId,
        ),
        externalId: targetId,
        ean: source.ean ?? '',
        product:
            source.componentName ??
            source.component_name ??
            source.productName ??
            source.product_name ??
            '',
        usage: Number(source.quantity) || 0,
        uom: source.uom ?? '',
        averageCost:
            source.averageCost ??
            source.average_cost ??
            '0',
        status: source.status ?? 'ACTIVE',
        originalStatus: source.status ?? 'ACTIVE',
        vatCode: '',
    };
};

const createDraftRow = (): ComponentRow => ({
    tempId: `component-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    rowType: 'draft',
    componentId: '',
    productTargetId: '',
    productId: '',
    externalId: '',
    ean: '',
    product: '',
    usage: 0,
    uom: '',
    averageCost: '0',
    status: 'ACTIVE',
    originalStatus: 'ACTIVE',
    vatCode: '',
});

const getToastErrorMessage = (
    error: any,
    fallback: string,
): string =>
    error?.err?.message ??
    error?.err?.error?.message ??
    error?.err?.detail ??
    error?.err?.code ??
    error?.message ??
    fallback;

const ProductComponentContainers = ({ productId }: Props) => {
    const { t } = useTranslation();

    const [productOptions, setProductOptions] = useState<ProductOption[]>([]);
    const [rows, setRows] = useState<ComponentRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [productsLoading, setProductsLoading] = useState(false);
    const [dropdownToOpen, setDropdownToOpen] = useState<string | null>(null);
    const [filteredProductOptions, setFilteredProductOptions] = useState<ProductOption[]>([]);
    const [editMode, setEditMode] = useState(false);
    const [productSidebarVisible, setProductSidebarVisible] = useState(false);
    const [productSidebarInitialName, setProductSidebarInitialName] = useState('');
    const [productSidebarRowId, setProductSidebarRowId] = useState<string | null>(null);

    const { showErrorMessage, showSuccessMessage } = useContext(
        MessageContext,
    ) as any;

    const isEditing = editMode;

    const searchProducts = (event: { query: string }) => {
        const query = event.query.trim().toLowerCase();

        setFilteredProductOptions(
            productOptions.filter(option =>
                option.label.toLowerCase().includes(query) ||
                option.externalId.toLowerCase().includes(query) ||
                option.ean.toLowerCase().includes(query),
            ),
        );
    };

    const statusOptions = useMemo(
        () => [
            { label: t('ResourceStatus.ACTIVE', 'Active'), value: 'ACTIVE' },
            { label: t('ResourceStatus.INACTIVE', 'Inactive'), value: 'INACTIVE' },
        ],
        [t],
    );


    const loadProducts = useCallback(async () => {
        setProductsLoading(true);

        try {
            const response = (await productInventoryService.list({
                first: 0,
                max: 1000,
                sort_field: 'name',
                sort_asc: true,
                only_active: true,
            } as any)) as {
                data?: ProductInventoryItemDto[];
                content?: ProductInventoryItemDto[];
            };

            const products = response?.data ?? response?.content ?? [];

            setProductOptions(
                products
                    .map(mapProductToOption)
                    .filter(
                        (option): option is ProductOption =>
                            option !== null &&
                            option.value !== productId &&
                            option.externalId !== productId,
                    ),
            );
        } catch (error: any) {
            showErrorMessage(
                getToastErrorMessage(
                    error,
                    t(
                        'productComponents.error.fetchProducts',
                        'Failed to load available products',
                    ),
                ),
            );

            setProductOptions([]);
        } finally {
            setProductsLoading(false);
        }
    }, [
        productId,
        showErrorMessage,
        t,
    ]);

    const loadProductComponents = useCallback(async () => {
        if (!productId) return;

        setLoading(true);

        try {
            const response =
                await productComponentsService.list(productId);

            const components = Array.isArray(response)
                ? response
                : ((response as any)?.content ?? []);

            setRows(
                components.map(mapProductComponentToRow),
            );
        } catch (error: any) {
            showErrorMessage(
                getToastErrorMessage(
                    error,
                    t(
                        'productComponents.error.fetch',
                        'Failed to load product components',
                    ),
                ),
            );

            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [
        productId,
        showErrorMessage,
        t,
    ]);

    useEffect(() => {
        void loadProducts();
        void loadProductComponents();
    }, [
        loadProductComponents,
        loadProducts,
    ]);

    const addDraftRow = () => {
        const draftRow = createDraftRow();

        setEditMode(true);
        setRows(prev => [...prev, draftRow]);
        setDropdownToOpen(draftRow.tempId);
    };

    useEffect(() => {
        if (!dropdownToOpen) return;

        const timeout = setTimeout(() => {
            setDropdownToOpen(null);
        }, 0);

        return () => clearTimeout(timeout);
    }, [dropdownToOpen]);

    const updateRow = (
        tempId: string,
        updater: (row: ComponentRow) => ComponentRow,
    ) => {
        setRows(prev =>
            prev.map(row => (row.tempId === tempId ? updater(row) : row)),
        );
    };

    const removeRow = (tempId: string) => {
        setRows(prev => prev.filter(row => row.tempId !== tempId));
    };

    const startEditAll = () => {
        setEditMode(true);
    };

    const startEditRow = (tempId: string) => {
        setEditMode(true);

        updateRow(tempId, row => ({
            ...row,
            rowType: 'edit',
        }));
    };

    const cancelEdit = () => {
        setEditMode(false);
        loadProductComponents();
    };

    const applyProductToRow = async (
        rowTempId: string,
        selectedOption: ProductOption,
    ) => {
        updateRow(rowTempId, current => ({
            ...current,
            componentId: '',
            productTargetId:
                selectedOption.externalId ||
                selectedOption.value,
            productId: selectedOption.systemId || selectedOption.value,
            externalId: selectedOption.externalId || selectedOption.value,
            ean: selectedOption.ean,
            product: selectedOption.label,
            uom: selectedOption.uom,
            status: selectedOption.status || 'ACTIVE',
            originalStatus: selectedOption.status || 'ACTIVE',
            vatCode: '',
            averageCost: '0',
        }));

        // Enrich the row with product data.
        try {
            const fullProduct =
                await productInventoryService.get(selectedOption.value);

            updateRow(rowTempId, current => ({
                ...current,
                vatCode: getProductVatCode(fullProduct),
                uom: getProductUom(fullProduct) || current.uom,
                ean: getProductEan(fullProduct) || current.ean,
                status: safeString(
                    fullProduct.status || current.status || 'ACTIVE',
                ),
            }));
        } catch {
            // Keep values from the product list.
        }
    };

    const clearProductFromRow = (
        rowTempId: string,
        text: string,
    ) => {
        updateRow(rowTempId, current => ({
            ...current,
            componentId: '',
            productTargetId: '',
            productId: '',
            externalId: '',
            ean: '',
            product: text,
            uom: '',
            averageCost: '0',
            status: 'ACTIVE',
            originalStatus: 'ACTIVE',
            vatCode: '',
        }));
    };

    const openProductSidebar = (
        rowTempId: string,
        query: string,
    ) => {
        setProductSidebarRowId(rowTempId);
        setProductSidebarInitialName(query.trim());
        setProductSidebarVisible(true);
    };

    const closeProductSidebar = () => {
        setProductSidebarVisible(false);
        setProductSidebarInitialName('');
        setProductSidebarRowId(null);
    };

    const handleProductCreated = (createdProduct?: unknown) => {
        if (!createdProduct || !productSidebarRowId) {
            closeProductSidebar();
            return;
        }

        const option = mapProductToOption({
            ...(createdProduct as ProductInventoryItemDto),
            useAsComponent: true,
        } as ProductInventoryItemDto);

        if (!option) {
            showErrorMessage(
                t(
                    'productComponents.error.createdProductInvalid',
                    'The created product could not be selected.',
                ),
            );
            closeProductSidebar();
            return;
        }

        setProductOptions(previous => [
            option,
            ...previous.filter(existing => existing.value !== option.value),
        ]);
        setFilteredProductOptions(previous => [
            option,
            ...previous.filter(existing => existing.value !== option.value),
        ]);

        void applyProductToRow(productSidebarRowId, option);
        closeProductSidebar();
    };

    const validateRow = (row: ComponentRow) =>
        Boolean(
            row.product &&
            row.usage > 0 &&
            (
                row.componentId ||
                row.productTargetId
            ),
        );

    const persistEditableRow = async (
        row: ComponentRow,
    ): Promise<ComponentRow> => {
        // A selected product can already have a Warehouse component.
        // In that case only update its status when necessary.
        if (row.componentId) {
            let nextStatus = row.status;

            if (
                row.status &&
                row.originalStatus &&
                row.status !== row.originalStatus
            ) {
                const updatedStatus =
                    await componentService.updateStatus(
                        row.componentId,
                        row.status as any,
                    );

                nextStatus =
                    updatedStatus?.status || row.status;
            }

            return {
                ...row,
                rowType: 'existing',
                status: nextStatus,
                originalStatus: nextStatus,
            };
        }

        if (row.productTargetId) {
            return {
                ...row,
                rowType: 'existing',
            };
        }

        throw new Error(
            `Missing component or product target for ${row.product}`,
        );
    };

    const saveAll = async () => {
        const invalidRow = rows.find(row => !validateRow(row));

        if (invalidRow) {
            showErrorMessage(
                t(
                    'productComponents.error.validation',
                    'Please select a component and enter usage greater than 0',
                ),
            );
            setRows(previous => [...previous]);
            return;
        }

        const duplicateTargetId = rows
            .map(row =>
                row.componentId
                    ? `component:${row.componentId}`
                    : row.productTargetId
                        ? `product:${row.productTargetId}`
                        : '',
            )
            .filter(Boolean)
            .find(
                (targetId, index, values) =>
                    values.indexOf(targetId) !== index,
            );

        if (duplicateTargetId) {
            showErrorMessage(
                t(
                    'productComponents.error.duplicate',
                    'The same component cannot be added more than once',
                ),
            );
            return;
        }

        setLoading(true);

        try {
            const persistedRows = await Promise.all(
                rows.map(row =>
                    row.rowType === 'draft' ||
                    row.rowType === 'edit'
                        ? persistEditableRow(row)
                        : Promise.resolve(row),
                ),
            );

            const relationPayload = persistedRows.map(row => {
                const quantity = String(row.usage);

                if (row.componentId) {
                    return {
                        componentId: row.componentId,
                        quantity,
                    };
                }

                if (row.productTargetId) {
                    return {
                        productId: row.productTargetId,
                        quantity,
                    };
                }

                throw new Error(
                    `Missing component or product target for ${row.product}`,
                );
            });

            // Persist either a legacy Warehouse component target or a catalog
            // product target. The backend requires exactly one target per row.
            await productComponentsService.set(
                productId,
                relationPayload as any,
            );

            setEditMode(false);

            // Always reload from the relationship endpoint. This verifies that
            // the saved components will still be present after a page refresh.
            await loadProductComponents();

            showSuccessMessage?.(
                t(
                    'productComponents.success.save',
                    'Product components saved successfully',
                ),
            );
        } catch (error: any) {
            showErrorMessage(
                getToastErrorMessage(
                    error,
                    t(
                        'productComponents.error.save',
                        'Failed to save product components',
                    ),
                ),
            );
        } finally {
            setLoading(false);
        }
    };

    const renderStatus = (row: ComponentRow) => (
        <span className="product-components-status">
            <i className="pi pi-check" />
            {t(`ResourceStatus.${row.status}`, row.status)}
        </span>
    );

    const renderReadOnlyCell = (value?: string | number | null) => (
        <span className="product-components-cell-text">
            {value === null || value === undefined || value === '' ? '—' : value}
        </span>
    );

    const renderActions = (row: ComponentRow) => {
        if (!isEditing) return null;

        if (row.rowType === 'existing') {
            return (
                <div className="product-components-row-actions">
                    <button
                        type="button"
                        className="product-components-icon-button"
                        aria-label={t('common.edit', 'Edit')}
                        disabled={loading}
                        onClick={() => startEditRow(row.tempId)}
                    >
                        <EditIcon />
                    </button>

                    <button
                        type="button"
                        className="product-components-icon-button"
                        aria-label={t('common.delete', 'Delete')}
                        disabled={loading}
                        onClick={() => removeRow(row.tempId)}
                    >
                        <RemoveLineIcon />
                    </button>
                </div>
            );
        }

        return (
            <div className="product-components-row-actions">
                <button
                    type="button"
                    className="product-components-icon-button"
                    aria-label={t('common.cancel', 'Cancel')}
                    disabled={loading}
                    onClick={() => removeRow(row.tempId)}
                >
                    <RemoveLineIcon />
                </button>
            </div>
        );
    };

    const renderRow = (row: ComponentRow, index: number) => {
        const editable = row.rowType === 'draft' || row.rowType === 'edit';

        return (
            <tr key={row.tempId}>
                <td>
                    {editable ? (
                        <InputText value={String(index + 1)} disabled />
                    ) : (
                        renderReadOnlyCell(index + 1)
                    )}
                </td>

                <td aria-label={t('productComponents.columns.id')}>
                    {editable ? (
                        <InputText value={row.productId} disabled />
                    ) : (
                        renderReadOnlyCell(row.productId)
                    )}
                </td>

                <td>
                    {editable ? (
                        <InputText value={row.ean} disabled />
                    ) : (
                        renderReadOnlyCell(row.ean)
                    )}
                </td>

                <td>
                    {editable ? (
                        <ResourceAutoComplete<ProductOption>
                            value={row.product}
                            suggestions={filteredProductOptions}
                            loading={productsLoading}
                            placeholder={
                                productsLoading
                                    ? t('common.loading', 'Loading...')
                                    : t('common.select', 'Select')
                            }
                            completeMethod={searchProducts}
                            getOptionLabel={option => option.label}
                            getOptionValue={option => option.value}
                            getAutoSelectMatchValues={option => [
                                option.label,
                                option.systemId,
                                option.externalId,
                                option.ean,
                            ]}
                            forceSelection
                            dropdown={false}
                            minLength={2}
                            delay={300}
                            invalid={!row.componentId && !row.productTargetId}
                            className={classNames({
                                'product-components-autocomplete--autofocus':
                                    row.tempId === dropdownToOpen,
                            })}
                            onSelect={option => {
                                void applyProductToRow(row.tempId, option);
                            }}
                            onClear={text => clearProductFromRow(row.tempId, text)}
                            emptyActionLabel={t(
                                'products.addNew',
                                'Add product',
                            )}
                            onEmptyAction={query =>
                                openProductSidebar(row.tempId, query)
                            }
                        />
                    ) : (
                        renderReadOnlyCell(row.product)
                    )}
                </td>

                <td>
                    {editable ? (
                        <InputNumber
                            value={row.usage}
                            min={0}
                            maxFractionDigits={4}
                            className={classNames({
                                'p-invalid': row.usage <= 0,
                            })}
                            onValueChange={event =>
                                updateRow(row.tempId, current => ({
                                    ...current,
                                    usage: event.value ?? 0,
                                }))
                            }
                        />
                    ) : (
                        renderReadOnlyCell(row.usage)
                    )}
                </td>

                <td>
                    {editable ? (
                        <InputText value={row.uom} disabled />
                    ) : (
                        renderReadOnlyCell(row.uom)
                    )}
                </td>

                <td>
                    {editable ? (
                        <InputText value={row.averageCost || '0'} disabled />
                    ) : (
                        renderReadOnlyCell(row.averageCost || '0')
                    )}
                </td>

                <td className="product-components-status-cell">
                    {editable ? (
                        <Dropdown
                            value={row.status}
                            options={statusOptions}
                            onChange={event =>
                                updateRow(row.tempId, current => ({
                                    ...current,
                                    status: event.value,
                                }))
                            }
                        />
                    ) : (
                        renderStatus(row)
                    )}
                </td>

                {isEditing && (
                    <td className="product-components-actions-cell">
                        {renderActions(row)}
                    </td>
                )}
            </tr>
        );
    };

    return (
        <div className="product-components-card">
            <div className="product-components-card__header">
                <h3>{t('productComponents.title')}</h3>

                {!isEditing ? (
                    <Button
                        className="product-components-card__top-button"
                        disabled={loading}
                        onClick={() => {
                            if (!rows.length) {
                                addDraftRow();
                            } else {
                                startEditAll();
                            }
                        }}
                    >
                        {rows.length ? (
                            <TopEditWhiteIcon className="icon" />
                        ) : (
                            <AddComponentsIcon className="icon" />
                        )}

                        <span className="p-button-label pl-2">
                            {rows.length
                                ? t('productComponents.actions.editComponent')
                                : t('productComponents.actions.addComponents')}
                        </span>
                    </Button>
                ) : (
                    <Button className="product-components-card__top-button" disabled>
                        <AddComponentsIcon className="icon" />
                        <span className="p-button-label pl-2">
                            {t('productComponents.actions.addComponents')}
                        </span>
                    </Button>
                )}
            </div>

            {!rows.length && !isEditing ? (
                <div className="product-components-empty">
                    <div className="product-components-empty__icon">
                        <EmptyComponentIcon />
                    </div>

                    <h4>{t('productComponents.empty.title')}</h4>

                    <div className="product-components-empty__message">
                        <WarningTriangleIcon
                            className="product-components-empty__warning-icon"
                            aria-hidden="true"
                        />
                        <p>{t('productComponents.empty.description')}</p>
                    </div>
                </div>
            ) : (
                <>
                    <div className="product-components-table-wrapper">
                        <table
                            className={classNames(
                                'product-components-table',
                                {
                                    'product-components-table--editing':
                                        isEditing,
                                },
                            )}
                        >

                            <thead>
                                <tr>
                                    <th>{t('productComponents.columns.no')}</th>
                                    <th>{t('productComponents.columns.id')}</th>
                                    <th>{t('productComponents.columns.ean')}</th>
                                    <th>{t('productComponents.columns.product')}</th>
                                    <th>{t('productComponents.columns.usage')}</th>
                                    <th>{t('productComponents.columns.uom')}</th>
                                    <th>{t('productComponents.columns.averageCost')}</th>
                                    <th>{t('productComponents.columns.status')}</th>
                                    {isEditing && <th />}
                                </tr>
                            </thead>

                            <tbody>{rows.map(renderRow)}</tbody>
                        </table>
                    </div>

                    {isEditing && (
                        <button
                            type="button"
                            className="product-components-add-next"
                            disabled={loading}
                            onClick={addDraftRow}
                        >
                            <i className="pi pi-plus-circle" />
                            <span>{t('productComponents.actions.addNextItem')}</span>
                        </button>
                    )}

                    {isEditing && (
                        <div className="product-components-footer-actions">
                            <Button
                                label={t('common.save', 'Save')}
                                loading={loading}
                                onClick={saveAll}
                                className="product-components-save-button"
                            />

                            <Button
                                label={t('common.cancel', 'Cancel')}
                                disabled={loading}
                                onClick={cancelEdit}
                                className="product-components-cancel-button"
                            />
                        </div>
                    )}
                </>
            )}
            <ProductSidebar
                visible={productSidebarVisible}
                viewMode="NEW"
                initialName={productSidebarInitialName}
                initialUseAsComponent
                focusName
                onHide={closeProductSidebar}
                onSuccess={handleProductCreated}
            />
        </div>
    );
};

export default ProductComponentContainers;
